import React, { useState, useEffect, useCallback } from 'react';
import './CommunicationsDashboard.css';

function getApiBase() {
  if (import.meta.env.REACT_APP_API_URL) return import.meta.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}
const API = getApiBase();

function adminSessionHeaders() {
  try {
    const t = localStorage.getItem('sessionToken');
    return t ? { 'X-Admin-Session': t } : {};
  } catch {
    return {};
  }
}

function commsFetch(url, options = {}) {
  return fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    ...options,
    headers: { ...adminSessionHeaders(), ...(options.headers || {}) },
  });
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') || iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const MAILBOX_FILTERS = [
  { id: 'all', label: 'All mailboxes' },
  { id: 'gmail', label: 'optiprimebc@gmail.com' },
  { id: 'optiprime', label: 'contact@optiprime.ai' },
];

const GENERATOR_LABELS = {
  daily_summary: 'Daily Summary',
  daily_report: 'Daily Gmail Report',
  gmail_reply: 'Gmail send / reply',
  optiguard: 'OptiGuard',
  compose: 'Compose / outreach',
  welcome: 'BETA welcome',
  contractor_welcome: 'Contractor / Mayan welcome',
  password_reset: 'Password reset',
  feedback: 'Feedback',
  signup_alert: 'New sign-up',
  colab: 'Co-Lab',
  other_contact: 'Other contact@',
};

function generatorTone(id) {
  if (id === 'optiguard') return 'warning';
  if (id === 'daily_summary' || id === 'daily_report') return 'sent';
  if (id === 'gmail_reply' || id === 'compose') return 'inbox';
  return 'other';
}

function sourceLabel(source) {
  if (source === 'gmail') return 'Gmail Sent';
  if (source === 'gmail_copy') return 'Gmail copy';
  if (source === 'optiprime') return 'Porkbun Sent';
  if (source === 'compose') return 'Compose log';
  return source;
}

export default function CommunicationsOutboundView() {
  const [catalog, setCatalog] = useState(null);
  const [mailbox, setMailbox] = useState('all');
  const [generator, setGenerator] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(1);
  const [emails, setEmails] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 40, total: 0, totalPages: 1 });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCatalog, setShowCatalog] = useState(true);

  const fetchCatalog = useCallback(() => {
    commsFetch(`${API}/api/communications/outbound/catalog`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setCatalog(d.catalog);
      })
      .catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page),
      limit: '40',
      mailbox,
      generator,
    });
    if (search.trim()) params.set('search', search.trim());
    try {
      const res = await commsFetch(`${API}/api/communications/outbound?${params}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not load outbound mail');
      setEmails(data.emails || []);
      setPagination(data.pagination || { page: 1, limit: 40, total: 0, totalPages: 1 });
      setCounts(data.counts || null);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [page, mailbox, generator, search]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const fetchDetail = (row) => {
    setSelectedId(row.id);
    setDetail(null);
    setDetailLoading(true);
    commsFetch(`${API}/api/communications/outbound/${row.source}/${row.sourceId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.email) setDetail(data.email);
        else setDetail(null);
      })
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const generatorChips = [
    { id: 'all', label: 'All types', n: counts?.all },
    ...Object.keys(GENERATOR_LABELS).map((id) => ({
      id,
      label: GENERATOR_LABELS[id],
      n: counts?.generators?.[id] || 0,
    })).filter((g) => g.id === generator || g.n > 0),
  ];

  return (
    <div className="comms-outbound-view">
      <p className="comms-sent-intro">
        Every outbound message we generate from <strong>optiprimebc@gmail.com</strong> (Gmail API)
        or <strong>contact@optiprime.ai</strong> (Porkbun SMTP, plus copies that land back in Gmail).
        Cron mail (Daily Summary, Daily Gmail Report, OptiGuard) appears here alongside Compose and replies.
      </p>

      <div className="comms-outbound-catalog-toggle">
        <button
          type="button"
          className={`comms-inbox-mailbox-btn ${showCatalog ? 'active' : ''}`}
          onClick={() => setShowCatalog((v) => !v)}
        >
          {showCatalog ? 'Hide generators' : 'Show generators'}
          {catalog?.total != null && (
            <span className="comms-inbox-filter-count">{catalog.total}</span>
          )}
        </button>
      </div>

      {showCatalog && catalog && (
        <div className="comms-outbound-catalog">
          <div className="comms-outbound-mailbox-kpis">
            {(catalog.mailboxes || []).map((mb) => (
              <button
                key={mb.id}
                type="button"
                className={`comms-outbound-kpi ${mailbox === mb.id ? 'active' : ''}`}
                onClick={() => {
                  setMailbox(mb.id);
                  setPage(1);
                  setSelectedId(null);
                  setDetail(null);
                }}
              >
                <div className="comms-outbound-kpi-label">{mb.id === 'gmail' ? 'GMAIL' : 'CONTACT@'}</div>
                <div className="comms-outbound-kpi-addr">{mb.address}</div>
                <div className="comms-outbound-kpi-value opo-mono-value">{(mb.count || 0).toLocaleString()}</div>
                <div className="comms-outbound-kpi-foot">{mb.transport}</div>
              </button>
            ))}
          </div>
          <div className="comms-outbound-gen-grid">
            {(catalog.generators || []).map((g) => (
              <button
                key={g.id}
                type="button"
                className={`comms-outbound-gen-card ${generator === g.id ? 'active' : ''}`}
                onClick={() => {
                  setGenerator(g.id);
                  setMailbox(g.mailbox || 'all');
                  setPage(1);
                  setSelectedId(null);
                  setDetail(null);
                }}
                title={`${g.trigger}\n${g.code}`}
              >
                <span className={`comms-inbox-cat comms-inbox-cat-${generatorTone(g.id)}`}>
                  {GENERATOR_LABELS[g.id] || g.name}
                </span>
                <span className="comms-outbound-gen-count opo-mono-value">{g.count || 0}</span>
                <span className="comms-outbound-gen-from">{g.from}</span>
                <span className="comms-outbound-gen-trig">{g.trigger}</span>
              </button>
            ))}
          </div>
          {catalog.otherGenerators?.length > 0 && (
            <p className="comms-outbound-other-note">
              Also generated (not these two mailboxes):{' '}
              {catalog.otherGenerators.map((g) => `${g.name} (${g.from})`).join(' · ')}
            </p>
          )}
        </div>
      )}

      <div className="comms-inbox-toolbar">
        <div className="comms-inbox-mailbox-toggle" role="tablist" aria-label="Mailbox">
          {MAILBOX_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={mailbox === f.id}
              className={`comms-inbox-mailbox-btn ${mailbox === f.id ? 'active' : ''}`}
              onClick={() => {
                setMailbox(f.id);
                setPage(1);
                setSelectedId(null);
                setDetail(null);
              }}
            >
              {f.label}
              {f.id !== 'all' && counts?.[f.id] != null && (
                <span className="comms-inbox-filter-count">{counts[f.id]}</span>
              )}
              {f.id === 'all' && counts?.all != null && (
                <span className="comms-inbox-filter-count">{counts.all}</span>
              )}
            </button>
          ))}
        </div>
        <div className="comms-inbox-filters" role="tablist" aria-label="Generator">
          {generatorChips.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={generator === f.id}
              className={`comms-inbox-filter-btn ${generator === f.id ? 'active' : ''}`}
              onClick={() => {
                setGenerator(f.id);
                setPage(1);
                setSelectedId(null);
                setDetail(null);
              }}
            >
              {f.label}
              {f.n != null && <span className="comms-inbox-filter-count">{f.n}</span>}
            </button>
          ))}
        </div>
        <form
          className="comms-sent-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchDraft.trim());
            setPage(1);
          }}
        >
          <input
            type="search"
            className="comms-sent-search-input"
            placeholder="Search subject, from, to…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
          <button type="submit" className="comms-sent-search-btn">Search</button>
        </form>
      </div>

      {error && <p className="comms-sent-error" role="alert">{error}</p>}

      <div className="comms-sent-layout comms-inbox-layout">
        <div className="comms-sent-list-wrap">
          {loading && emails.length === 0 && <div className="comms-loading">Loading outbound mail…</div>}
          {!loading && emails.length === 0 && (
            <div className="comms-empty">No outbound messages match these filters.</div>
          )}
          <ul className="comms-sent-list">
            {emails.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`comms-sent-row comms-inbox-row ${selectedId === row.id ? 'selected' : ''}`}
                  onClick={() => fetchDetail(row)}
                >
                  <span className="comms-inbox-row-top">
                    <span className={`comms-inbox-cat comms-inbox-cat-${generatorTone(row.generator)}`}>
                      {GENERATOR_LABELS[row.generator] || row.generator}
                    </span>
                    <span className="comms-outbound-source">{sourceLabel(row.source)}</span>
                  </span>
                  <span className="comms-sent-row-to">{row.toEmail || '(no recipient)'}</span>
                  <span className="comms-sent-row-subj">{row.subject || '(no subject)'}</span>
                  <span className="comms-sent-row-meta">
                    {row.fromEmail} · {formatWhen(row.sentAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {pagination.totalPages > 1 && (
            <div className="comms-sent-pager">
              <button
                type="button"
                disabled={pagination.page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>Page {pagination.page} / {pagination.totalPages}</span>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages || loading}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
        <div className="comms-sent-detail-wrap comms-inbox-detail-wrap">
          {!selectedId && (
            <p className="comms-sent-detail-placeholder">
              Select a message to read the outbound copy we generated.
            </p>
          )}
          {selectedId && detailLoading && <div className="comms-loading">Loading message…</div>}
          {selectedId && !detailLoading && detail && (
            <div className="comms-sent-detail comms-inbox-detail">
              <div className="comms-inbox-detail-head">
                <h4>{detail.subject || '(no subject)'}</h4>
              </div>
              <dl className="comms-sent-detail-dl">
                <dt>From</dt>
                <dd>{detail.fromName ? `${detail.fromName} <${detail.fromEmail}>` : (detail.fromEmail || '—')}</dd>
                <dt>To</dt>
                <dd>{detail.toEmail || '—'}</dd>
                <dt>Sent</dt>
                <dd>{formatWhen(detail.sentAt)}</dd>
                <dt>Type</dt>
                <dd>{GENERATOR_LABELS[detail.generator] || detail.generator}</dd>
                <dt>Source</dt>
                <dd>{sourceLabel(detail.source)}</dd>
              </dl>
              <div className="comms-inbox-body">
                {detail.bodyHtml ? (
                  <iframe
                    title="Outbound email body"
                    className="comms-inbox-body-frame"
                    sandbox=""
                    srcDoc={detail.bodyHtml}
                  />
                ) : (
                  <pre className="comms-sent-detail-body-plain">{detail.bodyText || '—'}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
